const MONO_URL_ACTIVE = ["true", "1", "yes"].includes((process.env.REACT_APP_MONO_URL_ACTIVE || "").toLowerCase());
const API_LINK =  MONO_URL_ACTIVE ? window.location.origin : process.env.REACT_APP_API_URL;
const API_BASE = API_LINK + "/api";
const DEFAULT_TIMEOUT = 10000; // 10s \\
const loadingListeners = new Set();

export const BASE_LINK = API_BASE;

function notifyLoading(delta) {
  loadingListeners.forEach((listener) => {
    try {
      listener(delta);
    } catch (err) {
      console.error("Loader listener failed", err);
    }
  });
}

export function subscribeApiLoading(listener) {
  loadingListeners.add(listener);
  return () => loadingListeners.delete(listener);
}

function normalizePayload(prefix, obj = {}) {
  return Object.entries(obj).reduce((acc, [k, v]) => {
    acc[`${prefix}${k}`] = v;
    return acc;
  }, {});
}

async function parseJsonSafe(res) {
  try {
    if (res.status === 204) return null;
    return await res.json();
  } catch {
    return { message: "Réponse serveur non JSON" };
  }
}

function hasFile(obj = {}) {
  if (!obj) return false;
  return Object.values(obj).some(v =>
    v instanceof File || v instanceof Blob || v instanceof FileList
  );
}

function buildBody({ params, fields, files }) {
  if (hasFile(files)) {
    const fd = new FormData();

    Object.entries(normalizePayload("p:", params)).forEach(([k, v]) =>
      fd.append(k, v == null ? "" : v)
    );

    Object.entries(normalizePayload("f:", fields)).forEach(([k, v]) =>
      fd.append(k, v == null ? "" : v)
    );

    Object.entries(files || {}).forEach(([k, value]) => {
      if (!value) return;

      if (value instanceof FileList) {
        [...value].forEach(f => fd.append(k, f));
      } else if (Array.isArray(value)) {
        value.forEach(f => fd.append(k, f));
      } else {
        fd.append(k, value);
      }
    });

    return fd;
  }

  return JSON.stringify({
    ...normalizePayload("p:", params),
    ...normalizePayload("f:", fields),
  });
}

export async function apiCall({ module, method = "GET", params = {}, fields = {}, files = {}, query = {}, timeout = DEFAULT_TIMEOUT, credentials = "include", headers = {}, signal: externalSignal,}) {
  if (!module) throw new Error("Missing API module");

  notifyLoading(1);

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeout);

  // Merge abort sources si fourni \\
  if (externalSignal) externalSignal.addEventListener("abort", () => controller.abort());

  // Query builder propre \\
  let urlQuery = { ...query };
  if (method === "GET") urlQuery = { ...urlQuery, ...normalizePayload("p:", params), ...normalizePayload("f:", fields), };

  const queryString = new URLSearchParams(urlQuery).toString();
  const url = `${API_BASE}/${module}${queryString ? "?" + queryString : ""}`;
  const isUpload = hasFile(files);
  let body;
  if (method !== "GET") body = isUpload ? buildBody({ params, fields, files }) : JSON.stringify({ ...normalizePayload("p:", params), ...normalizePayload("f:", fields) });

  try {
    let res;
    try {
      res = await fetch(url, {
        method,
        credentials,
        headers: {
          ...(isUpload ? {} : { "Content-Type": "application/json" }),
          ...headers,
        },
        body,
        signal: controller.signal,
      });
    } catch (err) {
      if (err.name === "AbortError") throw new Error("API timeout / aborted");
      throw new Error("Impossible de contacter le serveur");
    }

    const data = await parseJsonSafe(res);

    if (!res.ok) throw new Error(data?.error || data?.message || `Erreur API (${res.status})`);

    return data;
  } finally {
    clearTimeout(timer);
    notifyLoading(-1);
  }
}

export const API = {
  get: (module, params = {}, fields = {}, query = {}) => apiCall({ module, method: "GET", params, fields, query }),
  post: (module, params, files) => apiCall({ module, method: "POST", params, files }),
  put: (module, params, fields, files) => apiCall({ module, method: "PUT", params, fields, files }),
  delete: (module, params) => apiCall({ module, method: "DELETE", params }),
  API_LINK: BASE_LINK,
};